import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import './DescriptionList.css';

export interface DescriptionItem {
  label: string;
  value: ReactNode;
  /** The monospace treatment for machine strings: addresses, versions, ids. */
  mono?: boolean;
}

export interface DescriptionListProps {
  items: DescriptionItem[];
  /** Shown when no item has a value. */
  emptyMessage?: string;
}

/**
 * DescriptionList shows an item's properties as label/value pairs. Rows the
 * backend reported nothing for are omitted, because an empty row reads as
 * "this is none" — a different, and wrong, claim from "this was not read".
 */
export function DescriptionList({ items, emptyMessage }: DescriptionListProps) {
  const { t } = useTranslation('common');
  const present = items.filter(
    (item) => item.value !== undefined && item.value !== null && item.value !== '',
  );
  if (present.length === 0) {
    return (
      <p className="rs-description-list__empty">{emptyMessage ?? t('state.nothingReported')}</p>
    );
  }
  return (
    <dl className="rs-description-list">
      {present.map((item) => (
        <Fragment key={item.label}>
          <dt>{item.label}</dt>
          <dd className={item.mono ? 'rs-mono' : undefined}>{item.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}
